/* eslint-disable ember/classic-decorator-hooks */
import EmberObject, { set } from '@ember/object';

import FilterConstants from '../../filter-constants';
import * as CircuitUtils from '../utils/circuit';
import * as InductorToleranceUtils from '../tolerances/inductor-tolerance-utils';
import { computeNominalValue } from '../tolerances/tolerance-utils';

//
// The idea behind a these components (capacitor, resistor, inductor) is we have classes that encapsulate
// how each of them are created. There is  a c1 class that is a type of capacitor with the difference with this class being
// in how the commercial value is computed. Commercial (Nominal) value for capacitor is always the closest to the ideal value.
// c1 will adjust depending on whether it goes beyond its limits.
//
// Lifecycle: This component is created with the
// 1. ideal value (component value based from computation, aka. textbook component in the old app, or ideal value),
// 2. a multiplier to get the minimum limit.
// 3. a multiplier to get the maximum limit.
//
// The ideal value is not directly used in here. It's only used to get the limits which are in turn used to compute
// a value based from c1's current value in comparison to its minimum and maximum (rcRatio).
//
// Construction: Capacitor.create({minimum: minimum, maximum: maximum, rcRatio: rcRatio})
// Hash:
// hash = {
//              minimum: { number },
//              maximum: { number },
//              rcRatio: { number }
//        }
// Create: Capacitor.create(hash);
// @inputProperty { number } rcRatio - The value of c1 relative to its minimum and maximum logarithmically.
// @inputProperty { number } maximum - the capacitor instance's maximum limit
// @inputProperty { number } minimum - the capacitor instance's minimum limit
//
// @getterProperty { number } nominalValue - The computed commercial value. This matches the value of capacitors that are available in the market.
// @getterProperty { number[] } toleranceArray - The nominal value multiplied to each of the elements of the array. The array consists of different tolerance multipliers.
// @getterProperty { FilterConstants.capacitorTolerance } finalTolerance - If the desired tolerance fails to get the nominal, there's a process that goes through all the available tolerance options to get the nominal value.
// @getterProperty { FilterConstants.capacitorTolerance } desiredTolerance - the selected capacitor tolerance or the default if none.
// @getterProperty { bool } isCommercial - True if this component is already converted into a commercial capacitor. False if not.
//
// @computed { number } sizingAdjustedValue - The computed ideal value based from the limits and the rcRatio
// @computed { number } value - the ideal value if the component is not commercial, the nominal value if the component is already converted to a commercial one.
// @method { this } toCommercialComponent() - converts the component into its commercial version.
// @method { number } getNominalValue() - gets the nominal value. Can only be accessed when the component is already a commercial one.
export default class Inductor extends EmberObject {
  name = FilterConstants.inductorNames.l1;
  minimum = null;
  maximum = null;
  rcRatio = null;
  isCommercial = null;
  nominalValue = null;
  toleranceArray = null;
  finalTolerance = null;
  desiredTolerance = null;
  toleranceInput = null;

  init() {
    super.init();
    set(this, 'isCommercial', false);
    this.value = CircuitUtils.computeComponentValueFromMinMaxAndRcRatio(this.minimum, this.maximum, this.rcRatio);
  }

  toCommercialComponent(toleranceInput) {
    set(this, 'toleranceInput', toleranceInput);
    let valueAdjusted = CircuitUtils.computeComponentValueFromMinMaxAndRcRatio(
      this.minimum,
      this.maximum,
      this.rcRatio,
    );
    let tolInput = !toleranceInput ? CircuitUtils.createToleranceInputDefaults() : toleranceInput;
    // let nominalValue = InductorToleranceUtils.computeNominalValue(
    //   valueAdjusted,
    //   tolInput,
    //   FilterConstants.roundDirection.closest
    // );
    let nominalValue = computeNominalValue(valueAdjusted, tolInput.inductorPreferredSeries, 'closest');
    let toleranceValues = InductorToleranceUtils.getToleranceValues(nominalValue, tolInput.inductorTolerance);

    set(this, 'value', nominalValue);
    set(this, 'toleranceArray', toleranceValues.toleranceArray);
    set(this, 'finalTolerance', toleranceValues.finalTolerance);
    set(this, 'desiredTolerance', tolInput.inductorTolerance);
    set(this, 'isCommercial', true);

    return this;
  }

  getNominalValue() {
    if (!this.isCommercial) throw 'component is not yet commercial. Call toCommercialComponent() first.';

    return this.value;
  }

  copyComponent() {
    let result = Inductor.create({
      idealValue: this.idealValue,
      minimum: this.minimum,
      maximum: this.maximum,
      sizing: this.sizing,
    });
    set(result, 'value', this.value);
    set(result, 'isCommercial', this.isCommercial);
    if (this.isCommercial) {
      set(result, 'toleranceArray', this.toleranceArray);
      set(result, 'desiredTolerance', this.desiredTolerance);
      set(result, 'finalTolerance', this.finalTolerance);
    }

    return result;
  }
}
